import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    MenuItem,
    Select,
    TextField,
    Typography,
    useTheme,
} from '@mui/material'
import {
    CancelOutlined,
    CheckBox,
    CheckBoxOutlineBlank,
} from '@mui/icons-material'
import { useDiscussionForum } from '../../store/discussionforum.store'
import { Form, Formik, ErrorMessage, Field } from 'formik'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useUserStore } from '../../store/user.store'
import { useSearchParams } from 'react-router-dom'
import { Icons } from '../../utils/utilities'
import { toast } from 'react-toastify'
import api from '../../service/api'
import * as Yup from 'yup'
import CustomWysiwyg from '../common/CustomWysiwyg'
import { discussionAttachmentUploader, onDiscussionFilePasteOrDrop } from '../../utils/custom-editor-utilities'
import DataLoadingSpinner from '../common/DataLoadingSpinner'


let editorRef, filesList = []

const filePasteOrDropHandler = async ( file, mime ) => {
    const urlDetails = await onDiscussionFilePasteOrDrop( file, mime )
    filesList.push( urlDetails.src )
    return urlDetails
}


const handleFileInput = async ( type ) => {
    const input = document.createElement( 'input' )

    input.setAttribute( 'type', 'file' )
    input.setAttribute( 'accept', type === 'image' ? 'image/*' : "video/*" )
    input.click()

    input.onchange = async () => {
        var file = input.files[0]
        // var fileName = `${Date.now()}_${file.name}`
        if ( type === 'image' && !file.type?.includes( 'image' ) ) {
            toast( "Invalid image format!" )
            return ''
        }
        if ( type === 'video' && !file.type?.includes( 'video' ) ) {
            toast( "Invalid video format!" )
            return ''
        }

        try {
            if ( editorRef.current ) {
                var url = await discussionAttachmentUploader( type, file )
                filesList.push( url )
                console.log( filesList )

                if ( url ) {
                    const range = editorRef.current.getSelection()
                    editorRef.current.insertEmbed( range.index, type, url )
                    editorRef.current.setSelection( range.index + 1 )
                    setTimeout( () => {
                        editorRef.current.focus()
                    }, 10 )
                }
            }

        } catch ( err ) {
            console.log( err )
        }
    }
}

const modules = {
    toolbar: {
        container: [
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ size: ['normal', 'small', 'large'] }],
            [{ align: [] }],
            [{ script: 'sub' }, { script: 'super' }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'image', 'video'],
            ['clean'],
            ['code-block'],
        ],
        handlers: {
            image: async () => await handleFileInput( 'image' ),
            video: async () => await handleFileInput( 'video' )
        }
    }
}

const EditDiscussionDialog = ( { state, setState, getData, discussion } ) => {
    const [updatingDiscussion, setUpdatingDiscussion] = useState( false )
    const [subjectFilterOptions, setSubjectFilterOptions] = useState( [] )
    const [selectedSubjectsIds, setSelectedSubjectsIds] = useState( [] )
    const [existingFilesList, setExistingFilesList] = useState( [] )
    const [loadingSubjects, setLoadingSubjects] = useState( true )
    const [subjectFilter, setSubjectFilter] = useState( 1 )

    const DiscussionForumStore = useDiscussionForum()
    const subjectsInputContainerRef = useRef()
    const UserStore = useUserStore()
    const { palette, border } = useTheme()
    editorRef = useRef()

    const [searchParams, setSearchParams] = useSearchParams( { subjectSearchText: '' } )
    const subjectSearchText = searchParams.get( 'subjectSearchText' )

    const initialValues = {
        title: discussion.title || '',
        content: discussion.content || '',
        access: discussion.access || 'subject_specific',
        access_groups: discussion.access_groups || [],
        categories: discussion.categories || [],
    }

    const validationSchema = Yup.object().shape( {
        title: Yup.string().required( 'Discussion title is required' ),
        content: Yup.string().required( 'Content is required' ).test( "CHECK_CONTENT", "Please enter discussion content", val => {
            if ( !val || val?.trim() === "" || val?.trim()?.replace( /\s/gi, "" ) === `<p></p>` || val?.trim()?.replace( /\s/gi, "" ) === `<p><br></p>` ) return false
            return true
        } ),
        access: Yup.string()
            .required( 'Access type is required' )
            .oneOf( ['global', 'subject_specific'] ),
        access_groups: Yup.array()
            .required( 'Access groups are required' )
            .min( 1, 'Discussion should have at least one access group' ),
        categories: Yup.array()
            .required( 'Categories are required' )
            .min( 1, 'Discussion should have at least one category' ),
    } )


    const setSearchParamValue = ( key, val ) => {
        setSearchParams(
            ( prev ) => {
                prev.set( key, val )
                return prev
            },
            { replace: true }
        )
    }

    const setCategories = ( setFieldValue, val ) => {
        setFieldValue( 'categories', val )
    }

    const handleClose = ( resetForm ) => {
        if ( resetForm ) resetForm()
        setSelectedSubjectsIds( [] )
        setState( false )
        setSearchParamValue( 'subjectSearchText', '' )
    }

    const handleKeyUp = ( e, setFieldValue, categories ) => {
        if ( e.key === 'Enter' && e.target.value.trim() !== '' ) {
            e.preventDefault()
            setCategories( setFieldValue, [...categories, e.target.value] )
            e.target.value = ''
        }
    }

    const removeCategory = ( index, setFieldValue, categories ) => {
        let copy = [...categories]
        copy.splice( index, 1 )
        setCategories( setFieldValue, copy )
    }

    const subjectsFilterFunc = ( s ) => {
        const subjectSearchTextPattern = new RegExp( subjectSearchText?.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' ), 'i' )
        if ( UserStore.getUser.user_role === 'STUDENT' ) {
            if ( UserStore.getUserSubjectsDetails.yearWise )
                return +s.current_year === +subjectFilter && ( s.subject_name?.match( subjectSearchTextPattern ) || s.subject_code?.match( subjectSearchTextPattern ) )
            else
                return +s.current_sem === +subjectFilter && ( s.subject_name?.match( subjectSearchTextPattern ) || s.subject_code?.match( subjectSearchTextPattern ) )
        } else {
            return s.ac_year === subjectFilter && ( s.subject_name?.match( subjectSearchTextPattern ) || s.subject_code?.match( subjectSearchTextPattern ) )
        }
    }

    const handleSemesterChange = ( e ) => {
        setSubjectFilter( e.target.value )
    }

    const toggleSubjectInclusion = ( sub, value, setFieldValue ) => {
        if ( selectedSubjectsIds.includes( sub.subject_id ) ) {
            let index = null
            for ( let k = 0; k < value.length; k++ ) {
                const currentSub = value[k]
                if ( sub.subject_id === currentSub.subject_id ) {
                    index = k
                    break
                }
            }
            if ( index > -1 ) {
                removeSubject( index, value, setFieldValue )
            }
        } else {
            setFieldValue( 'access_groups', [...value, sub] )
            setSelectedSubjectsIds( [...value, sub].map( ( s ) => s.subject_id ) )
        }
    }

    const deleteFileFromSpaces = async ( url ) => {
        try {
            await api.discussionForum.deleteFile( { url } )
            const fileUrlIndex = filesList.indexOf( url )
            if ( fileUrlIndex > -1 )
                filesList.splice( fileUrlIndex, 1 )
        } catch ( err ) {

        }
    }

    const removeSubject = ( index, subjects, setFieldValue ) => {
        let copy = [...subjects]
        copy.splice( index, 1 )
        setFieldValue( 'access_groups', copy )
        setSelectedSubjectsIds( copy.map( ( s ) => s.subject_id ) )
    }

    const getSubjects = useCallback( async () => {
        setLoadingSubjects( true )
        if ( UserStore.getErrorsStatuses?.subjects === true )
            await UserStore.fetchUserSubjects()
        const subjectsDetails = UserStore.getUserSubjectsDetails
        if ( UserStore.getUser.user_role === 'STUDENT' ) {
            if ( subjectsDetails.yearWise )
                setSubjectFilterOptions( subjectsDetails.years )
            else
                setSubjectFilterOptions( subjectsDetails.semesters )
        } else {
            setSubjectFilterOptions( subjectsDetails.academicYears )
            setSubjectFilter( subjectsDetails.academicYears[0] )
        }

        setLoadingSubjects( false )
    }, [UserStore] )

    const handleEditorValueChange = ( val, setFieldValue, previousValue ) => {
        setFieldValue( 'content', val )
        for ( const url of filesList ) {
            if ( !val?.includes( url ) ) {
                console.log( "Deleted file :", url )
                deleteFileFromSpaces( decodeURIComponent( url ) )
            }
        }
    }

    const updateDiscussion = async ( values, { resetForm } ) => {
        setUpdatingDiscussion( true )

        const success = await DiscussionForumStore.updateDiscussion(
            discussion._id,
            values
        )
        if ( success ) {
            handleClose( resetForm )
            for ( const url of [...filesList, ...existingFilesList] ) {
                if ( !values?.content?.includes( url ) ) {
                    console.log( "Deleting: ", url )
                    deleteFileFromSpaces( decodeURIComponent( url ) )
                }
            }
        }
        setUpdatingDiscussion( false )
    }

    useEffect( () => {
        try {
            const re = /https:\/\/[-a-z]+\.[a-zA-Z0-9]+\.(cdn\.)?digitaloceanspaces\.com\/(temp\/)?discussions\/(images|videos)\/[a-zA-Z0-9@%()_-]+\.\w+\/?/gm
            setExistingFilesList( discussion.content.match( re ) || [] )
            setSelectedSubjectsIds( discussion.access_groups?.map( sub => sub.subject_id ) || [] )
        } catch ( err ) { }
    }, [discussion] )

    useEffect( () => {
        const getData = async () => {
            await getSubjects()
        }
        getData()
    }, [getSubjects] )

    return (
        <Dialog open={state} slotProps={{
            paper: { sx: { width: '90%', maxWidth: '850px' } }
        }} >
            <Box display='flex' alignItems='center' justifyContent='space-between' gap='20px' padding='10px 20px' borderBottom={border[1]} >
                <Typography variant='h6'>Edit {discussion.title}</Typography>
                <IconButton onClick={() => handleClose()}>
                    {Icons.default.CloseIcon}
                </IconButton>
            </Box>
            <DialogContent sx={{ padding: 0 }}>
                <Box>
                    <Formik initialValues={initialValues} onSubmit={updateDiscussion} validationSchema={validationSchema} >
                        {( { values, setFieldValue, resetForm } ) => {
                            return (
                                <Form>
                                    <Box padding='20px' display='flex' flexDirection='column' gap='10px' >
                                        <Box>
                                            <Typography variant='subtitle1' fontWeight='500'> Discussion title </Typography>
                                            <Typography marginTop='-5px' gutterBottom variant='body2' fontSize='12px' color='textSecondary' >
                                                Summarize the discussion topic in the short title.
                                            </Typography>
                                            <Field as={TextField} placeholder='Eg: State value is not changing in ReactJs' type='text' size='small' name='title' variant='outlined' fullWidth />
                                            <Typography variant='subtitle2' color='error'> <ErrorMessage name='title' />{' '} </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant='subtitle1' fontWeight='500'> Discussion description </Typography>
                                            <Typography marginTop='-5px' gutterBottom variant='body2' fontSize='12px' color='textSecondary' > Describe the discussion topic in detail. </Typography>
                                            <Box>
                                                <CustomWysiwyg
                                                    filePasteOrDropHandler={filePasteOrDropHandler}
                                                    height='200px'
                                                    onChange={e => handleEditorValueChange( e, setFieldValue, values.content )}
                                                    setParentEditorRef={( refVal ) => { editorRef.current = refVal?.current }}
                                                    value={values.content} modules={modules} placeholder='Share your thoughts...'
                                                />
                                                <Typography variant='subtitle2' color='error'> <ErrorMessage name='content' /> </Typography>
                                            </Box>
                                        </Box>
                                        <Box>
                                            <Typography variant='subtitle1' fontWeight='500'> Specify discussion categories </Typography>
                                            <Box flexWrap='wrap' border={`2px dotted ${palette.borderColor}`} padding="10px" alignItems='center' gap='10px' borderRadius='5px'  >
                                                <TextField fullWidth size='small' onKeyDown={( e ) => handleKeyUp( e, setFieldValue, values.categories )} type='text' placeholder='Type categories and press enter to add.' />
                                                {values.categories.length > 0 && <Box marginTop="10px" display="flex" gap="10px" flexWrap="wrap" alignItems="center">
                                                    {values.categories.map( ( category, index ) => ( <Box key={index} bgcolor={`${palette.primary.light}11`} fontSize="12px" display='flex' gap='10px' borderRadius='5px' width='fit-content' alignItems='center' padding='2px 5px 2px 10px' border={`1px solid ${palette.primary.light}55`} >
                                                        <Typography variant='subtitle2' fontSize="12px" component="em"> {category} </Typography>
                                                        <IconButton size='small' onClick={() => removeCategory( index, setFieldValue, values.categories )} > <CancelOutlined fontSize='small' /> </IconButton>
                                                    </Box> ) )}
                                                </Box>}
                                                {values.categories.length === 0 && <Typography textAlign="center" padding="20px" variant='body2' fontSize="12px" >
                                                    No categories added yet. Add categories to help organize discussions.
                                                </Typography>}
                                            </Box>
                                            <Typography variant='subtitle2' color='error'> <ErrorMessage name='categories' /></Typography>
                                        </Box>
                                        {values.access === 'subject_specific' && (
                                            <Box>
                                                <Typography variant='subtitle1' fontWeight='500'> Select access groups(subjects) </Typography>
                                                <Typography marginTop='-5px' gutterBottom variant='body2' fontSize='12px' color='textSecondary' >
                                                    Only selected groups can participate in the discussion.
                                                </Typography>

                                                <Box padding='10px' ref={subjectsInputContainerRef} gap='10px' borderRadius='5px' border={`2px dotted ${palette.borderColor}`} >
                                                    {values.access_groups.length > 0 && <Box display="flex" gap="10px" flexWrap="wrap" marginBottom="10px">
                                                        {values.access_groups.map( ( selSub, index ) => (
                                                            <Box key={selSub.subject_id} bgcolor={`${palette.primary.light}11`} display='flex' gap='10px' width='fit-content' alignItems='center' padding='0 5px' borderRadius="5px" border={`1px solid ${palette.primary.light}55`} >
                                                                <Box>
                                                                    <Typography component="em" textTransform="capitalize" fontSize="12px" variant='subtitle2'>
                                                                        {selSub.subject_name?.toLowerCase()}
                                                                    </Typography>
                                                                    <Typography component="em" display="block" fontSize='10px'>
                                                                        {selSub.subject_code}
                                                                    </Typography>
                                                                </Box>
                                                                <IconButton
                                                                    size='small'
                                                                    onClick={() =>
                                                                        removeSubject(
                                                                            index,
                                                                            values.access_groups,
                                                                            setFieldValue
                                                                        )
                                                                    }
                                                                >
                                                                    <CancelOutlined fontSize='small' />
                                                                </IconButton>
                                                            </Box>
                                                        ) )}
                                                        {values.access_groups.length === 0 && <Typography textAlign="center" padding="20px" variant='body2' fontSize="12px" >
                                                            No access groups added yet. Add subjects to specify access.
                                                        </Typography>}
                                                    </Box>}
                                                    <Box sx={{ zIndex: 5 }} display="flex" gap="10px" alignItems="center" >
                                                        <TextField onChange={( e ) => setSearchParamValue( 'subjectSearchText', e.target.value )} size='small' fullWidth type='text' placeholder='Search subjects...' />
                                                        {UserStore.getUser['user_role'] === 'STUDENT' && (
                                                            <Select size='small' variant='outlined' onChange={handleSemesterChange} value={subjectFilter}>
                                                                {subjectFilterOptions.map( ( filterOption, i ) => (
                                                                    <MenuItem dense sx={{ fontSize: '14px' }} value={filterOption} key={filterOption}>
                                                                        Semester {filterOption}
                                                                    </MenuItem>
                                                                ) )}
                                                            </Select>
                                                        )}
                                                    </Box>
                                                    <Box maxHeight="150px" marginTop="10px" className="custom-scrollbar" overflow="auto">
                                                        {UserStore.getUserSubjectsDetails?.currentSubjects
                                                            .filter( subjectsFilterFunc )
                                                            .map( ( sub ) => {
                                                                return (
                                                                    // <FormControlLabel sx={{ "&:hover": { background: palette.rowHover }, borderBottom: "1px solid #d3d3d3", margin: 0, padding: 0 }} size="small" onChange={( e ) => toggleSubjectInclusion( e, sub, values.access_groups, setFieldValue )} control={<Checkbox sx={{ height: "40px", width: "40px", transform: "scale(0.9)" }} />} label={`${sub.subject_name}(${sub.subject_name_short})`} />
                                                                    <Box key={sub.subject_id}>
                                                                        <Box
                                                                            onClick={() => toggleSubjectInclusion( sub, values.access_groups, setFieldValue )}
                                                                            display='flex'
                                                                            gap='10px'
                                                                            sx={{
                                                                                cursor: 'pointer',
                                                                                '&:hover': {
                                                                                    background: palette.rowHover,
                                                                                },
                                                                            }}
                                                                            padding='2px 10px'
                                                                            alignItems='center'
                                                                            key={sub.subject_id}
                                                                            borderRadius="5px"
                                                                        // border={border[1]}
                                                                        >
                                                                            {!selectedSubjectsIds.includes( sub.subject_id ) ? (
                                                                                <CheckBoxOutlineBlank
                                                                                    sx={{
                                                                                        color: 'darkgrey',
                                                                                        '&:hover': {
                                                                                            color: 'GrayText',
                                                                                        },
                                                                                    }}
                                                                                    fontSize='small'
                                                                                />
                                                                            ) : (
                                                                                <CheckBox fontSize='small' />
                                                                            )}
                                                                            <Typography variant='body2' textTransform="capitalize" fontWeight={selectedSubjectsIds.includes( sub.subject_id ) ? "500" : "300"} >
                                                                                {`${sub.subject_name?.toLowerCase()} (`}<strong>{`${sub.subject_code})`}</strong>
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                )
                                                            } )}
                                                    </Box>
                                                    {UserStore.getUserSubjectsDetails?.currentSubjects
                                                        .filter( subjectsFilterFunc ).length === 0 && <Typography display="block" borderRadius="5px" border={border[1]} textAlign="center" variant='subtitle2' component="em" fontSize="12px" padding="10px 20px"  >
                                                            Subjects are not available!
                                                        </Typography>}
                                                    {loadingSubjects && <Typography display="block" borderRadius="5px" border={border[1]} textAlign="center" variant='subtitle2' component="em" fontSize="12px" padding="10px 20px"  >
                                                        <DataLoadingSpinner waitingMessage="Loading subjects..." size={20} />
                                                    </Typography>}
                                                </Box>
                                                <Typography variant='subtitle2' color='error'>
                                                    <ErrorMessage name='access_groups' />{' '}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box padding='10px 20px' marginY="20px" display='flex' justifyContent='flex-end' gap='10px' >
                                        <Button disabled={updatingDiscussion} type='submit' sx={{ textTransform: 'capitalize' }} variant='contained' disableElevation startIcon={updatingDiscussion ? ( <CircularProgress size={14} /> ) : ( Icons.default.EditIcon )} color='primary' >
                                            {updatingDiscussion
                                                ? 'Updating discussion...'
                                                : 'Update Discussion'}
                                        </Button>
                                        <Button disabled={updatingDiscussion} startIcon={Icons.default.CloseIcon} onClick={() => handleClose( resetForm )} sx={{ textTransform: 'capitalize' }} variant='outlined' color='error' >
                                            Cancel
                                        </Button>
                                    </Box>
                                </Form>
                            )
                        }}
                    </Formik>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default EditDiscussionDialog


// const handleAccessTypeChange = async ( e, setFieldValue ) => {
//     const val = e.target.value
//     setFieldValue( 'access', val )
//     if ( val === 'global' ) {
//         setFieldValue( 'access_groups', [
//             {
//                 subject_name: 'all',
//                 subject_code: 'ALL',
//                 subject_name_short: 'all',
//                 subject_id: '-1',
//             },
//         ] )
//     } else {
//         setFieldValue( 'access_groups', [] )
//         if ( Object.keys( subjects ).length === 0 ) await getSubjects()
//     }
//     setSelectedSubjectsIds( [] )
// }
